import * as Sentry from '@sentry/browser';
import logger from '@/utils/logger';
import type { ChatConversation } from '@/types/chatbot.types';

/**
 * Storage key prefix for conversations
 */
const STORAGE_KEY_PREFIX = 'ai_assistant_conversations_';

/**
 * Maximum number of conversations to keep per station
 */
export const MAX_CONVERSATIONS_PER_STATION = 5;

/**
 * Maximum number of messages to keep per conversation
 */
export const MAX_MESSAGES_PER_CONVERSATION = 30;

/**
 * Generates a storage key for a station's conversations
 */
export const getStorageKey = (stationId: string): string => {
  return `${STORAGE_KEY_PREFIX}${stationId}`;
};

/**
 * Deserializes conversations from JSON, converting date strings back to Date objects
 */
const deserializeConversations = (conversations: ChatConversation[]): ChatConversation[] => {
  return conversations.map(conv => ({
    ...conv,
    createdAt: new Date(conv.createdAt),
    updatedAt: new Date(conv.updatedAt),
    messages: conv.messages.map(msg => ({
      ...msg,
      timestamp: new Date(msg.timestamp),
    })),
  }));
};

/**
 * Loads conversations from localStorage for a given station
 * Returns empty array if no conversations found or on error
 */
export const loadConversationsFromStorage = (stationId: string): ChatConversation[] => {
  try {
    const storageKey = getStorageKey(stationId);
    const storedData = localStorage.getItem(storageKey);

    if (!storedData) {
      return [];
    }

    const conversations: ChatConversation[] = JSON.parse(storedData);
    return deserializeConversations(conversations);
  } catch (error) {
    logger('Error loading conversations from localStorage:', error);
    Sentry.captureException(new Error(`Error loading conversations from localStorage: ${error}`));
    return [];
  }
};

/**
 * Saves conversations to localStorage for a given station
 */
export const saveConversationsToStorage = (stationId: string, conversations: ChatConversation[]): void => {
  try {
    const storageKey = getStorageKey(stationId);
    localStorage.setItem(storageKey, JSON.stringify(conversations));
  } catch (error) {
    logger('Error saving conversations to localStorage:', error);
    Sentry.captureException(new Error(`Error saving conversations to localStorage: ${error}`));
  }
};

/**
 * Limits the number of messages in a conversation to the maximum allowed
 * Returns the limited conversation
 */
export const limitConversationMessages = (
  conversation: ChatConversation,
  maxMessages: number = MAX_MESSAGES_PER_CONVERSATION,
): ChatConversation => {
  if (conversation.messages.length <= maxMessages) {
    return conversation;
  }

  return {
    ...conversation,
    messages: conversation.messages.slice(-maxMessages),
    updatedAt: new Date(),
  };
};

/**
 * Sorts conversations by updatedAt descending (most recent first)
 */
export const sortConversationsByRecent = (conversations: ChatConversation[]): ChatConversation[] => {
  return [...conversations].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
};

/**
 * Limits the number of conversations to keep, keeping only the most recent ones
 * Returns the limited conversations array
 */
export const limitConversations = (
  conversations: ChatConversation[],
  maxConversations: number = MAX_CONVERSATIONS_PER_STATION,
): ChatConversation[] => {
  if (conversations.length <= maxConversations) {
    return conversations;
  }

  const sorted = sortConversationsByRecent(conversations);
  return sorted.slice(0, maxConversations);
};

/**
 * Updates or adds a conversation in the conversations array
 * Returns the updated conversations array
 */
export const upsertConversation = (
  conversations: ChatConversation[],
  conversation: ChatConversation,
): ChatConversation[] => {
  const existingIndex = conversations.findIndex(conv => conv.id === conversation.id);

  if (existingIndex >= 0) {
    const updated = [...conversations];
    updated[existingIndex] = conversation;
    return updated;
  }

  return [...conversations, conversation];
};

/**
 * Loads conversations from storage and applies limits
 * This is a convenience function that combines loading and limiting
 */
export const loadAndLimitConversations = (
  stationId: string,
  maxConversations: number = MAX_CONVERSATIONS_PER_STATION,
): ChatConversation[] => {
  const conversations = loadConversationsFromStorage(stationId);
  const limited = limitConversations(conversations, maxConversations);

  // If conversations were limited, save back to storage
  if (limited.length < conversations.length) {
    saveConversationsToStorage(stationId, limited);
  }

  return limited;
};
